import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { Mono, Badge, Button, IconButton, TONE, markColor } from '../components/primitives';
import { Card, Eyebrow, PeriodStepper } from '../components/card';
import { ConfirmDialog } from '../components/dialog';
import { InlineStatus, LoadError, useToast } from '../components/feedback';
import { CorrectionModal } from '../components/corrections';
import { EditDayDialog } from '../components/editDay';
import { listMine, type Correction } from '../lib/timeCorrections';
import { CompHoursModal } from '../components/compHours';
import {
  listMine as listMyComp,
  listCategories,
  type CompHours,
  type Category,
} from '../lib/compHours';
import { KpiGrid, KpiTile } from '../components/domain';
import { CheckoutModal } from '../components/timer';
import {
  addDays,
  dayIndex,
  fmtDayKey,
  isoDay,
  orgDateKey,
  orgToday,
  weekColumns,
  weekHeader,
  weekStart,
  weekdayLabels,
} from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import { overtimeSeconds } from '../lib/overtime';
import { isWorkday } from '../lib/workdays';
import { MonthlySummary } from '../components/MonthlySummary';
import { DayClockLog } from '../components/dayClockLog';
import { loadSummaries, type MonthSummary } from '../lib/monthlySummary';
import {
  checkIn,
  checkOut,
  elapsedOf,
  fmtTime,
  loadDay,
  loadLoggedOn,
  loadStaleSession,
  onApprovedLeaveToday,
  resumeClock,
  staleRecords,
  summarize,
  takeBreak,
  type ClockEntry,
  type ClockResult,
  type ClockState,
} from '../lib/dayclock';
import { fmtDuration } from '../lib/duration';
import { useBelow } from '../lib/useMediaQuery';

type Entry = {
  id: string;
  project_id: string | null;
  started_at: string;
  // created_at is what the SERVER buckets an entry into a day by (org_today of
  // it drives attendance, and a correction's work_date). The grid below still
  // columns by started_at; corrections must not, or a request could land on a
  // different day than the one the person clicked.
  created_at: string;
  seconds: number;
  status: 'running' | 'paused' | 'logged';
  system_closed: boolean;
};
type Proj = { id: string; name: string; code: string | null; tone: string };

// DAYS = ['Mon'…'Sun'] lived here. This screen never selected week_start at all,
// so it had no wire to the setting to hardcode away from — see docs/UI-CONVENTIONS.md §2.
// A private hex TONE map lived here too: "accent" projects drew the retired lime
// and iris/sky were off-token (TS-15). Project swatches are marks: markColor.

// weekStartOf() lived here — the third private copy of the same hardcoded Monday.
// Use weekStart(weekStartsOn, orgToday(tz), offset) from lib/dates.

// A figure that is summed — a cell, a total, a tile — is decimal hours, one way
// (UI-CONVENTIONS §2). The grid wrote zero as "·", the tiles as "0.0" (TS-08).
const hrs = (h: number): string => fmtDuration(h * 3600, 'decimal');

// Which clock action is in flight. The button that was pressed shows it; the
// others wait. It was one boolean that dimmed every button to 60%.
type Action = 'check in' | 'take a break' | 'resume' | 'check out';

// A row of hours is the day clock or a project, named and marked the same way
// in the week table and the phone's day list.
function Source({ pid, projects }: { pid: string; projects: Record<string, Proj> }) {
  const isGeneral = pid === 'general';
  const p = projects[pid];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--hf-space-2)' }}>
      {/* markColor for both, so General and an "accent" project are one blue,
          not the accent fill beside the accent text colour. */}
      <span
        style={{
          width: 8,
          height: 8,
          flexShrink: 0,
          borderRadius: 'var(--hf-radius-xs)',
          background: markColor(isGeneral ? 'accent' : (p?.tone ?? 'iris')),
        }}
      />
      <span style={{ color: 'var(--hf-ink)' }}>
        {isGeneral ? 'Day clock · general hours' : (p?.name ?? 'Unassigned')}
      </span>
      {!isGeneral && p?.code && <Mono dim>{p.code}</Mono>}
    </span>
  );
}

// ── The day clock — big, plain check-in / check-out for everyone ──────────────
function DayClockCard({
  state,
  arrival,
  tz,
  totalSeconds,
  busy,
  staleDay,
  notice,
  onCheckIn,
  onBreak,
  onResume,
  onCheckOut,
  onCloseStale,
  leaveToday,
  actionsRef,
}: {
  state: ClockState;
  arrival: string | null;
  /** The org's zone. The arrival is read in it, as Check-in history below reads
   *  the same session: in the browser's zone the two disagreed for anyone abroad. */
  tz: string;
  totalSeconds: number;
  busy: Action | null;
  /** "Mon 28 Sep" — the earlier day a session is still open on, if there is one. */
  staleDay: string | null;
  /** The last action did not land, and why. */
  notice: string | null;
  onCheckIn: () => void;
  onBreak: () => void;
  onResume: () => void;
  onCheckOut: () => void;
  onCloseStale: () => void;
  leaveToday: boolean; // today is approved leave — warn, never block
  /** The buttons' row, so the screen can put focus back on the day's next step. */
  actionsRef: RefObject<HTMLDivElement | null>;
}) {
  const live = state === 'in';
  // The clock is text, so the live state reads --hf-accent-text: --hf-accent is
  // the fill, 3.1:1 as text on dark.
  const tone = live
    ? 'var(--hf-accent-text)'
    : state === 'break'
      ? TONE.warning.fg
      : 'var(--hf-ink)';
  // No session is open today, but one still is from an earlier day. The card
  // used to say "Ready when you are" over it and refuse the check-in in silence.
  const stale = staleDay != null && (state === 'none' || state === 'out');
  // On a phone held upright the buttons take the card's width, so the day's
  // next step is a wide target under the thumb rather than a box at the left.
  // Two that no longer fit side by side stack in their order: the break
  // button above Check out, in both states. Take a break becomes Resume where
  // it was pressed, so neither button moves when the state flips and a second
  // tap can't land on Check out. Below sm, not md: from 480 (a phone on its
  // side, 450px tall) the button fits beside the text, and a 640px-wide one
  // only cost height.
  const phone = useBelow('sm');
  // A label that still can't fit (Check out of Mon 28 Sep, at 320) wraps
  // inside its button rather than push the card wide.
  const fill: CSSProperties | undefined = phone
    ? { flex: '1 1 auto', justifyContent: 'center', whiteSpace: 'normal', textAlign: 'center' }
    : undefined;

  const headline: CSSProperties = {
    fontFamily: 'var(--hf-font-display)',
    fontSize: 'var(--hf-text-2xl)',
    fontWeight: 'var(--hf-weight-regular)',
    letterSpacing: 'var(--hf-track-display)',
    color: 'var(--hf-ink)',
  };
  // Bigger than any other button on the screen, and built from the same Button
  // (it was a hand-rolled copy with its own gradient, TS-18). The one that was
  // pressed shows the spinner; the other waits, and cannot be pressed mid-flight.
  const big = (
    label: string,
    icon: 'play' | 'pause' | 'stop',
    onClick: () => void,
    variant: 'primary' | 'secondary',
    action: Action,
  ) => (
    <Button
      size="xl"
      variant={variant}
      icon={icon}
      onClick={onClick}
      loading={busy === action}
      disabled={busy != null && busy !== action}
      focusableWhenDisabled
      style={fill}
    >
      {label}
    </Button>
  );

  return (
    <section
      className="hf-glass"
      aria-labelledby="hf-day-clock"
      style={{
        borderRadius: 'var(--hf-radius-card)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(120% 160% at 88% -30%, color-mix(in srgb, var(--hf-accent) ${live ? 20 : 10}%, transparent), transparent 55%)`,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'relative',
          padding: 'var(--hf-space-5)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--hf-space-5)',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            flex: '1 1 240px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-2)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 'var(--hf-space-2)',
            }}
          >
            {/* A real heading: the card was titled by a styled span, and "Day
                clock" also named the log at the foot of the page (TS-20). */}
            <Eyebrow as="h2" id="hf-day-clock">
              Day clock
            </Eyebrow>
            {/* The state, announced when it changes: it is the receipt for a
                check-in, a break and a check-out. */}
            <span role="status">
              {stale ? (
                <Badge tone="warning" dot>
                  Still checked in
                </Badge>
              ) : state === 'in' ? (
                <Badge tone="accent" dot>
                  On the clock
                </Badge>
              ) : state === 'break' ? (
                <Badge tone="warning" dot>
                  On break
                </Badge>
              ) : state === 'out' ? (
                <Badge tone="success" dot>
                  Checked out
                </Badge>
              ) : (
                <Badge tone="neutral">Not checked in</Badge>
              )}
            </span>
          </div>

          {stale ? (
            <div style={headline}>Your session from {staleDay} is still open</div>
          ) : (
            <>
              {state === 'none' && <div style={headline}>Ready when you are</div>}
              {(state === 'in' || state === 'break') && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: 'var(--hf-space-3)',
                  }}
                >
                  <Mono
                    style={{
                      fontSize: 'var(--hf-text-4xl)',
                      fontWeight: 'var(--hf-weight-medium)',
                      color: tone,
                      lineHeight: 'var(--hf-leading-none)',
                    }}
                  >
                    {fmtDuration(totalSeconds, 'clock')}
                  </Mono>
                  <Mono dim style={{ fontSize: 'var(--hf-text-md)' }}>
                    since {fmtTime(arrival, tz)}
                  </Mono>
                </div>
              )}
              {state === 'out' && (
                <div style={headline}>
                  You worked{' '}
                  <Mono style={{ fontSize: 'inherit', color: 'var(--hf-accent-text)' }}>
                    {fmtDuration(totalSeconds, 'clock')}
                  </Mono>{' '}
                  today
                </div>
              )}
            </>
          )}

          {/* ink-2: these are sentences to read, not labels (TS-06). */}
          <div
            style={{
              fontSize: 'var(--hf-text-sm)',
              color: 'var(--hf-ink-2)',
              lineHeight: 'var(--hf-leading-body)',
              maxWidth: '60ch',
            }}
          >
            {stale
              ? 'It was never checked out, so today can’t start. Check out of it, and today starts fresh.'
              : state === 'none'
                ? 'Press check in when you start your day. We’ll track your hours and mark you present.'
                : state === 'in'
                  ? `Checked in at ${fmtTime(arrival, tz)}. Take a break any time — your hours keep adding up.`
                  : state === 'break'
                    ? 'You’re on a break — the clock is paused. Resume when you’re back.'
                    : // Not "marked present" on a leave day: what happens to the
                      // day is the leave's outcome, stated below (TS-21).
                      `Checked in at ${fmtTime(arrival, tz)}${leaveToday ? '.' : ' · marked present for today.'}`}
          </div>

          {notice && <InlineStatus tone="error">{notice}</InlineStatus>}

          {/* Working on approved leave is allowed. The server settles the day when
              you check out: a short stint keeps the leave, about half a day makes it
              a half day, a full day gives it back entirely. */}
          {leaveToday && !stale && (
            <InlineStatus tone="warning">
              {state === 'out'
                ? 'You were on approved leave today. The leave day is settled against what you worked: a full day returns it, about half a day returns half, and a short stint leaves it as it was.'
                : 'You’re on approved leave today. Working is fine — when you check out we’ll return the leave day to your balance, or half of it if you only work part of the day.'}
            </InlineStatus>
          )}
        </div>

        {/* An earlier day's session is always offered its check-out: the
            server caps the owner's check-out of it as the overnight close does
            (20261009230000), so a forgotten day no longer lands whole on
            attendance, overtime and leave. The confirm says what it records. */}
        <div
          ref={actionsRef}
          tabIndex={-1}
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 'var(--hf-space-3)',
            ...(phone ? { flex: '1 1 100%' } : null),
          }}
        >
          {stale ? (
            <Button
              size="xl"
              variant="primary"
              icon="stop"
              loading={busy === 'check out'}
              onClick={onCloseStale}
              style={fill}
            >
              Check out of {staleDay}
            </Button>
          ) : (
            <>
              {state === 'none' && big('Check in', 'play', onCheckIn, 'primary', 'check in')}
              {/* One primary per state: the day's next step. Check out is
                  primary, never rose — ending the day is routine (§3). */}
              {state === 'in' && (
                <>
                  {big('Take a break', 'pause', onBreak, 'secondary', 'take a break')}
                  {big('Check out', 'stop', onCheckOut, 'primary', 'check out')}
                </>
              )}
              {state === 'break' && (
                <>
                  {big('Resume', 'play', onResume, 'primary', 'resume')}
                  {big('Check out', 'stop', onCheckOut, 'secondary', 'check out')}
                </>
              )}
              {state === 'out' && big('Check in again', 'play', onCheckIn, 'secondary', 'check in')}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export function TimesheetsScreen() {
  const { session } = useAuth();
  const uid = session?.user.id;
  const toast = useToast();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [projects, setProjects] = useState<Record<string, Proj>>({});
  const [day, setDay] = useState<ClockEntry[]>([]);
  // A session left open on an earlier org day. Today's read never shows it, and
  // one_active_time_entry refuses every check-in until it is closed.
  const [stale, setStale] = useState<ClockEntry | null>(null);
  // The day-clock time already logged on that session's day, which the server
  // takes off the contracted day it may record. null until known, or unread.
  const [staleLogged, setStaleLogged] = useState<number | null>(null);
  const [closingStale, setClosingStale] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState<Action | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // A read that failed is not an empty week and not "not checked in" (TS-14).
  // Two flags, so a week that would not load never takes the day clock with it.
  const [clockFailed, setClockFailed] = useState(false);
  const [weekFailed, setWeekFailed] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = this week, -1 = last week…
  // The latest refresh() call. Two quick steps back raced: whichever week's read
  // landed last filled the grid and the Overtime row, under the other's dates.
  const latest = useRef(0);
  // Server-computed overtime hours per weekday, in week-start order, from overtime_ledger.
  // null, not 0: accrue_overtime() writes a row for every FINALIZED day, even a
  // day with no overtime at all, so a missing row means the day has not been
  // settled yet — not that nothing was banked. Zeroing it is what made an open
  // day read "Overtime · today 0.0h" beside "vs standard 108%".
  const [otHours, setOtHours] = useState<(number | null)[]>([
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ]);
  const [otOn, setOtOn] = useState(true);
  const [leaveToday, setLeaveToday] = useState(false);
  const [mine, setMine] = useState<Correction[]>([]);
  // The org-day the correction dialog is open for, if any.
  const [correcting, setCorrecting] = useState<string | null>(null);
  // R5 — hours that never touched the clock. Claimed here because this is where a
  // person already comes to reconcile their month.
  const [comp, setComp] = useState<CompHours[]>([]);
  // Bumped by every refresh() so the day clock log below refetches with it — a
  // filed correction or claim, a check-out, a cancel.
  const [loadNonce, setLoadNonce] = useState(0);
  const [countWeekends, setCountWeekends] = useState(false);
  // null until the answer is known. A weekend's overtime is 0 or the WHOLE
  // day depending on this, so guessing it would be guessing the number.
  const [holidayToday, setHolidayToday] = useState<boolean | null>(null);
  const [compCats, setCompCats] = useState<Category[]>([]);
  const [claiming, setClaiming] = useState(false);
  // This week's target, from the same loader the monthly summary uses. RLS scopes
  // it to one row — the viewer's own. undefined until it lands (the figures show
  // the stub, not the org's 8h passed off as this person's contract); null when
  // it landed with no row for them.
  const [week, setWeek] = useState<MonthSummary | null>();
  const [targetFailed, setTargetFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // `hours` is the ORG's standard day. It used to be fetched here and derived
  // separately; it is the fallback for anyone with no per-person contract.
  const { weekStartsOn, tz, hours: std, workdays, editDay: editDayRollout } = useOrgDates();
  // Edit day for everyone (policy.editDay.rollout): the flag on a day asks for
  // the whole day in one request, instead of the per-session correction.
  const editDayOn = editDayRollout === 'everyone';
  const phone = useBelow('md');
  const wkStart = useMemo(
    () => weekStart(weekStartsOn, orgToday(tz), weekOffset),
    [weekStartsOn, tz, weekOffset],
  );

  // The week's target: the viewer's contracted day × the days they were actually
  // expected to work — org workdays, minus holidays, minus approved paid leave.
  // It used to be a flat `std.perWeek`, which told someone on leave all week that
  // they were 40 hours short.
  useEffect(() => {
    let live = true;
    void loadSummaries({
      from: isoDay(wkStart),
      to: isoDay(addDays(wkStart, 6)),
      tz,
      org: std,
      workdays,
      today: orgDateKey(Date.now(), tz),
    }).then((rows) => {
      if (!live) return;
      setTargetFailed(!!rows.failed);
      setWeek(rows.failed ? undefined : (rows.find((r) => r.profileId === uid) ?? null));
    });
    return () => {
      live = false;
    };
  }, [wkStart, tz, std, workdays, uid, attempt]);

  // Resolves to today's rows as just read (null if that read failed), for the
  // caller that has to decide something off them before the state lands.
  const refresh = useCallback(async (): Promise<ClockEntry[] | null> => {
    if (!supabase || !uid) {
      setLoading(false);
      return null;
    }
    // ±1 day either side: started_at is a timestamptz and the exact org-day
    // boundary is applied by the column map below, which is cheaper and more
    // obviously right than reconstructing an IANA offset here.
    const call = ++latest.current;
    const start = weekStart(weekStartsOn, orgToday(tz), weekOffset);
    const fetchFrom = addDays(start, -1);
    const end = addDays(start, 8);
    const [te, pr, dayRows, [staleRow, logged], onLeave, org, hol, ot, myCorr, myComp, cats] =
      await Promise.all([
        supabase
          .from('time_entries')
          .select(
            'id, project_id, started_at, created_at, ended_at, seconds, status, system_closed',
          )
          .eq('user_id', uid)
          .gte('started_at', fetchFrom.toISOString())
          .lt('started_at', end.toISOString()),
        supabase.from('projects').select('id, name, code, tone'),
        loadDay(uid, tz),
        // A day-clock session's check-out records what was left of its day, so
        // that day's logged time is read with it. A project timer's cap is the
        // plain day.
        loadStaleSession(uid, tz).then(
          async (s) => [s, s && s.project_id == null ? await loadLoggedOn(uid, tz, s) : 0] as const,
        ),
        onApprovedLeaveToday(uid, tz),
        // Only `policy` now — the working-day columns come from useOrgDates(), so
        // this screen cannot drift from the rest of the app on what a day is.
        supabase.from('org_settings').select('policy').maybeSingle(),
        // Today only — 0 or 1 rows. hol_read opens holidays to any signed-in user,
        // so this needs no permission the screen does not already have. Without it
        // the live figure below cannot tell a public holiday (banks in FULL) from
        // an ordinary weekend (banks nothing).
        supabase.from('holidays').select('date').eq('date', orgDateKey(Date.now(), tz)),
        // Overtime comes from the ledger, not from hours-minus-standard. On a
        // non-workday accrue_overtime() counts either NOTHING (countWeekends off) or
        // the WHOLE day — never hours-minus-8 — so recomputing it here would
        // contradict the overtime leave actually credited. RLS ot_read allows self-read.
        supabase
          .from('overtime_ledger')
          .select('date, ot_seconds')
          .eq('profile_id', uid)
          .gte('date', isoDay(start))
          .lt('date', isoDay(end)),
        listMine(uid),
        listMyComp(uid),
        listCategories(),
      ]);
    // A newer call is on its way and will set all of this; today's rows are
    // still today's for the caller that asked.
    if (call !== latest.current) return dayRows;
    // A stale read that failed is the clock's failure too: without it, "Check
    // in" may be the one action the server is bound to refuse (UI-CONVENTIONS §7).
    setClockFailed(dayRows === null || staleRow === undefined);
    if (dayRows) setDay(dayRows);
    setStale(staleRow ?? null);
    setStaleLogged(logged);
    // A confirm asked for a session that is no longer open must not wait to pop
    // up over the next one.
    if (!staleRow) setClosingStale(false);
    setLeaveToday(onLeave);
    setLoading(false);
    setLoadNonce((n) => n + 1);
    // Every one of these used to be `data ?? []`: an outage rendered "No time
    // logged for this week" and an overtime of zero. On any failure the week
    // keeps nothing on screen and says so; what was there before is a week ago's
    // truth at best.
    const bad =
      !!(te.error || pr.error || org.error || hol.error || ot.error) ||
      !!myCorr.failed ||
      !!myComp.failed;
    setWeekFailed(bad);
    if (bad) return dayRows;

    setEntries((te.data as Entry[]) ?? []);
    const map: Record<string, Proj> = {};
    ((pr.data as Proj[]) ?? []).forEach((p) => (map[p.id] = p));
    setProjects(map);
    setMine(myCorr);
    setComp(myComp);
    setCompCats(cats);
    // Bucket the ledger into the org's week, by calendar key. This used to be a
    // Math.round of a ms delta while the grid twenty lines down used Math.floor —
    // the same screen disagreed with itself by a column across a DST change.
    const otCol = weekColumns(weekStartsOn, orgToday(tz), weekOffset);
    const otDays: (number | null)[] = [null, null, null, null, null, null, null];
    ((ot.data as { date: string; ot_seconds: number }[]) ?? []).forEach((r) => {
      const di = otCol.get(r.date); // overtime_ledger.date is org-local already
      if (di !== undefined) otDays[di] = (r.ot_seconds || 0) / 3600;
    });
    setOtHours(otDays);

    const o = org.data as {
      day_start: string;
      day_end: string;
      lunch_mins: number;
      workdays: Record<string, boolean>;
      policy: {
        overtime?: { enabled?: boolean; hoursPerLeave?: number; countWeekends?: boolean };
      } | null;
    } | null;
    // accrue_overtime() writes no ledger row at all when accrual is off, so an
    // empty tile would otherwise read as a truthful "no overtime".
    setOtOn(
      (o?.policy?.overtime?.enabled ?? true) && (o?.policy?.overtime?.hoursPerLeave ?? 8) > 0,
    );
    // Already in the row above — this screen simply never looked at it.
    setCountWeekends(o?.policy?.overtime?.countWeekends ?? false);
    setHolidayToday((hol.data?.length ?? 0) > 0);
    return dayRows;
    // tz and weekStartsOn are read throughout this callback and arrive from
    // useOrgDates() AFTER the first render. Without them here the callback keeps
    // its first-render closure forever, so every date key above — including the
    // holiday query's — is computed from the default timezone and never
    // corrected. Invisible only while the default happens to be the real value.
  }, [uid, weekOffset, tz, weekStartsOn]);

  // initial load + live sync (timer pill and this screen share state)
  useEffect(() => {
    void refresh();
    if (!supabase || !uid) return;
    const sb = supabase;
    const ch = sb
      .channel(`timesheets:${uid}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'time_entries', filter: `user_id=eq.${uid}` },
        () => void refresh(),
      )
      .subscribe();
    // Same-tab sync: the topbar timer pill dispatches hf:timesync on every clock
    // action, so this screen refreshes immediately without waiting on the
    // Realtime round-trip (which the pill and this card subscribe to separately).
    const onSync = () => void refresh();
    window.addEventListener('hf:timesync', onSync);
    return () => {
      void sb.removeChannel(ch);
      window.removeEventListener('hf:timesync', onSync);
    };
  }, [uid, refresh]);

  const retry = () => {
    setAttempt((n) => n + 1);
    void refresh();
  };

  // Another week is Loading… until its read lands. The grid and its Overtime
  // row went on showing the old week's figures under the new week's dates.
  const toWeek = (w: number) => {
    if (w === weekOffset) return;
    setLoading(true);
    setWeekOffset(w);
  };

  const clock = useMemo(() => summarize(day, now), [day, now]);

  // A confirmed check-out unmounts the button that opened its dialog, so the
  // focus the dialog had just handed back fell to <body> and the keyboard
  // started again from the top of the page. It goes to the button that took its
  // place (Check in again; Check in, after closing an earlier day's session),
  // unless the person has already moved somewhere else. It goes to the row the
  // button sits in, not the button: a focused Check in again took the Space that
  // woke a sleeping PC as a click and checked the person back in unseen (bug
  // report, 2026-10-09). One Tab still reaches the button.
  const actionsRef = useRef<HTMLDivElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    if (document.activeElement === document.body) actionsRef.current?.focus();
  }, [clock.state, stale]);

  // A phone turned on its side with the correction sheet open crosses 768: the
  // list's flag that opened the sheet unmounts and the table's flag mounts, so
  // the dialog had nothing to hand focus back to and it fell to <body>. The
  // same day's flag, in whichever layout is up, takes it.
  const wasCorrecting = useRef<string | null>(null);
  useEffect(() => {
    const day = wasCorrecting.current;
    wasCorrecting.current = correcting;
    if (correcting || !day || document.activeElement !== document.body) return;
    document.querySelector<HTMLElement>(`[data-correct-day="${day}"] button`)?.focus();
  }, [correcting]);

  // 1s tick whenever anything is running — the day-clock (clock.state==='in')
  // OR a project timer this week — so the week's figures + grid tick live too, not
  // just the day-clock card. The clock.state disjunct keeps the day card ticking
  // even on a past-week view (whose `entries` exclude today's running row).
  // A stale running session ticks too, so the figure its confirm states stays
  // live, even on a week view that does not hold it.
  const anyRunning =
    clock.state === 'in' ||
    stale?.status === 'running' ||
    entries.some((e) => e.status === 'running');
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [anyRunning]);

  // One clock action at a time. A refusal is said on the card: every one of
  // these used to be awaited, refreshed past, and forgotten.
  const act = async (what: Action, fn: () => Promise<ClockResult>) => {
    if (busy) return;
    setBusy(what);
    setNotice(null);
    try {
      const r = await fn();
      const rows = await refresh();
      window.dispatchEvent(new Event('hf:timesync')); // let the topbar pill re-sync
      if (r.ok) return;
      refocus.current = false; // nothing was unmounted: the opener still has focus
      if (r.reason !== 'open_session') setNotice(`Couldn’t ${what}. ${r.message}`);
      // The one-open-session index said no. If the re-read found today's session
      // (another tab, the pill), the card now shows it and that is the answer.
      // Otherwise it is one left open on an earlier day, which the card now
      // shows too, with the way out of it.
      else if (!rows?.some((e) => e.status !== 'logged'))
        setNotice('Couldn’t check in: a session from an earlier day is still open.');
    } finally {
      setBusy(null);
    }
  };
  const onCheckIn = () => uid && void act('check in', () => checkIn(uid));
  const confirmCheckout = (note: string) => {
    setCheckoutOpen(false);
    const active = clock.active;
    if (active)
      void act('check out', () => {
        refocus.current = true;
        return checkOut(active, note);
      });
  };
  const onBreak = () => clock.active && void act('take a break', () => takeBreak(clock.active!));
  const onResume = () => clock.active && void act('resume', () => resumeClock(clock.active!));
  // A session that crossed the org's midnight with this page open is an earlier
  // day's now, and the server caps its check-out (20261009230000) where the
  // wrap-up would say "worked today". The re-read moves it to the stale card,
  // whose button takes the focus, and its confirm says what lands.
  const onCheckOut = () => {
    if (!clock.active) return;
    if (orgDateKey(clock.active.created_at, tz) < orgDateKey(Date.now(), tz)) {
      refocus.current = true;
      // Asked for now, shown once the re-read finds it; a re-read that finds
      // none (the overnight close got there first) withdraws it.
      setClosingStale(true);
      void refresh();
    } else setCheckoutOpen(true);
  };

  const staleDayLabel = stale
    ? fmtDayKey(orgDateKey(stale.created_at, tz), { weekday: true })
    : null;
  // What the check-out records, as the server will cap it (staleRecords). null
  // when the contracted day or that day's logged time could not be read: the
  // confirm then states the rule, not a number it would have to guess.
  const perDaySec = week ? Math.round(week.perDay * 3600) : null;
  const records =
    stale && perDaySec != null && staleLogged != null
      ? staleRecords(stale, perDaySec, staleLogged, now)
      : null;
  const ran = stale ? elapsedOf(stale, now) : 0;
  const closeStale = () => {
    setClosingStale(false);
    if (!stale) return;
    void act('check out', async () => {
      refocus.current = true;
      const r = await checkOut(stale);
      if (r.ok)
        toast.push({
          tone: 'success',
          message: `Checked out of ${staleDayLabel}. You can check in for today.`,
        });
      return r;
    });
  };

  // grid[projectId][dayIndex] = hours
  const { grid, projOrder, dayTotals, weekTotal } = useMemo(() => {
    const col = weekColumns(weekStartsOn, orgToday(tz), weekOffset);
    const g: Record<string, number[]> = {};
    const dayT = [0, 0, 0, 0, 0, 0, 0];
    let total = 0;
    for (const e of entries) {
      // Org calendar key, not a ms delta. The old clamp silently filed anything
      // outside the window into column 0 rather than dropping it.
      const di = col.get(orgDateKey(e.started_at, tz));
      if (di === undefined) continue; // from the ±1 day over-fetch
      const pid = e.project_id ?? 'general';
      const hours = elapsedOf(e, now) / 3600; // folds the live segment while running
      (g[pid] ??= [0, 0, 0, 0, 0, 0, 0])[di] += hours;
      dayT[di] += hours;
      total += hours;
    }
    return { grid: g, projOrder: Object.keys(g), dayTotals: dayT, weekTotal: total };
  }, [entries, weekStartsOn, tz, weekOffset, now]);

  // The week's figures read DAILY on the current week (today's hours vs the per-day
  // standard); on a past week they fall back to that week's totals.
  const sameWeek = weekOffset === 0;
  const todayIdx = dayIndex(orgDateKey(now, tz), weekStartsOn); // org day, org week
  const basisHours = sameWeek ? dayTotals[todayIdx] : weekTotal;
  // The viewer's own contracted day, falling back to the org's for anyone HR has
  // not given a contract.
  const perDay = week?.perDay ?? std.perDay;
  // A whole week's target is NOT perDay × workdayCount: holidays and approved paid
  // leave come out of it first. That is the entire point of the change.
  const basisStd = sameWeek ? perDay : (week?.requiredHours ?? std.perWeek);
  const targetPct = basisStd ? Math.round((basisHours / basisStd) * 100) : 0;
  // Overtime is whatever the server banked, on the same today/week basis as its
  // sibling figures. `basisHours - basisStd` was wrong on any non-workday.
  // A week total sums the days that HAVE settled; an unsettled day contributes
  // nothing rather than poisoning the sum.
  const settled = otHours.filter((h): h is number => h != null);
  // A PAST week sums to 0 when it has no rows, and that 0 is true. Every day in it
  // has closed: close_stale_sessions sweeps a forgotten check-out nightly, and
  // accrue_overtime writes a row when each day closes — so a day with no row had
  // no time entries at all (leave, a weekend, before joining) and banked nothing.
  // #114 turned this into `null` → "— · settles at check-out", which is false for
  // a week that ended days ago: there is no session to check out of, and no row
  // will ever arrive. Only TODAY can be genuinely unsettled, and that is the
  // `sameWeek` branch, which keeps its null.
  const overtime = sameWeek ? otHours[todayIdx] : settled.reduce((a, b) => a + b, 0);
  // `otPending` lived here — `sameWeek && overtime == null && anyRunning`. Both
  // halves were wrong. `overtime == null` could not happen, because a ledger row
  // of 0 was written at check-in; and `anyRunning` is false on a BREAK, since a
  // paused entry is not 'running' — so even once the row stopped being written,
  // the hint would have vanished the moment somebody stepped away. Replaced by
  // `dayOpen` (clock.active != null) and `otNote` below.

  // ── Corrections ────────────────────────────────────────────────────────────
  const todayKey = orgDateKey(now, tz);

  // ── Overtime while it is still being worked ────────────────────────────────
  // The ledger settles a day only once it closes (20260911100000), which is why
  // the tile had nothing to show mid-shift. This computes the same band the
  // server will, from the clock that is still running.
  const dayOpen = clock.active != null;
  // A session that began on an EARLIER org day belongs to the nightly sweeper:
  // close_stale_sessions caps a forgotten entry at the contracted day at 00:15,
  // so the browser's elapsed time for it is both wrong and still climbing.
  const staleDay = dayOpen && orgDateKey(clock.active!.created_at, tz) !== todayKey;
  // accrue_overtime adds approved compensated hours to the day before taking the
  // band, so a day spent at a client still banks. Pending claims are an ask.
  const compToday = comp.reduce(
    (t, c) => t + (c.status === 'approved' && c.date === todayKey ? c.hours : 0),
    0,
  );
  const holidaySet = useMemo(
    () => (holidayToday ? new Set([todayKey]) : new Set<string>()),
    [holidayToday, todayKey],
  );
  // Unknowable until the holiday answer AND this person's contracted day land —
  // `perDay` silently falls back to the org's 8h before `week` arrives, which
  // would read 2h low for a 6h contract.
  const otKnown = holidayToday != null && week != null && !staleDay;
  const liveOt = otKnown
    ? overtimeSeconds({
        iso: todayKey,
        // clock.totalSeconds buckets by created_at, exactly as stamp_attendance
        // does — unlike dayTotals, which buckets by started_at and is reassigned
        // to now() on every pause/resume (20260728130000:82).
        workedSeconds: clock.totalSeconds + compToday * 3600,
        standardSeconds: Math.round(perDay * 3600),
        workdays,
        holidays: holidaySet,
        countWeekends,
      }) / 3600
    : null;
  // Prefer the live figure whenever the day is open. A settled row for a RE-OPENED
  // day (out at lunch, back in after) holds only the morning, so trusting it there
  // is the confident-low case.
  const useLive = sameWeek && otOn && (dayOpen || overtime == null);
  const otShown = useLive ? liveOt : overtime;
  // The one honest confident zero: provable before any measurement exists.
  const weekendOff =
    otKnown && !holidayToday && !countWeekends && !isWorkday(todayKey, workdays, holidaySet);
  const otNote = !otOn
    ? 'accrual off'
    : staleDay
      ? 'closes overnight'
      : otShown == null
        ? 'settles at check-out'
        : !useLive
          ? null
          : weekendOff
            ? 'weekend · excluded'
            : holidayToday
              ? 'holiday · in full'
              : 'provisional';
  // The grid's Overtime row: the ledger day by day, with today's live figure
  // while the day is open, so the row and the figure above say the same.
  // null stays null — the stub, never 0.
  const otDays = otHours.map((h, i) => (useLive && i === todayIdx ? otShown : h));
  const otKnownDays = otDays.filter((h): h is number => h != null);
  // A past week's 0 is true (see `overtime`); this week's, with nothing known, is not.
  const otWeek =
    sameWeek && otKnownDays.length === 0 ? null : otKnownDays.reduce((a, b) => a + b, 0);
  const dayKeyFor = (i: number) => isoDay(addDays(wkStart, i));
  const pendingByDay = useMemo(() => {
    const m: Record<string, Correction> = {};
    for (const c of mine) if (c.status === 'pending') m[c.work_date] = c;
    return m;
  }, [mine]);
  // Bucketed by created_at, not started_at: that is the column the server derives
  // work_date from, so the dialog offers exactly the entries a request can name.
  const correctingEntries = useMemo(
    () => (correcting ? entries.filter((e) => orgDateKey(e.created_at, tz) === correcting) : []),
    [correcting, entries, tz],
  );

  if (!supabase)
    return (
      <div style={{ padding: 'var(--hf-space-7)', color: 'var(--hf-ink-3)' }}>
        Supabase not configured.
      </div>
    );

  // The week is two reads, the entries and the target they are measured
  // against. A failed entries read takes the grid and every figure: one error,
  // one Retry, where the grid would be. A failed target read takes only the
  // figures that are measured against it (they stub on `week === undefined`,
  // never the org's fallback) and says so beside them. The grid stays: it does
  // not need the target, and it is the only way to flag a day for correction.
  const noFigures = loading || weekFailed;
  const labels = weekdayLabels(weekStartsOn);
  // Today's column, so the eye finds it without reading seven headers (TS-12).
  const todayCol = (i: number): CSSProperties | null =>
    sameWeek && i === todayIdx ? { background: 'var(--hf-accent-wash)' } : null;

  return (
    <div
      style={{
        // The phone gutter is the shell's own (Menu, the clock bar): 16px.
        padding: phone ? 'var(--hf-space-4)' : 'var(--hf-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-4)',
        maxWidth: 1240,
        margin: '0 auto',
      }}
    >
      {clockFailed ? (
        <Card label="Day clock">
          <LoadError what="your day clock" onRetry={retry} />
        </Card>
      ) : (
        <DayClockCard
          state={clock.state}
          arrival={clock.arrival}
          tz={tz}
          totalSeconds={clock.totalSeconds}
          busy={busy}
          staleDay={staleDayLabel}
          notice={notice}
          onCheckIn={onCheckIn}
          onBreak={onBreak}
          onResume={onResume}
          onCheckOut={onCheckOut}
          onCloseStale={() => {
            setNow(Date.now()); // the figure the confirm states is as of now
            setClosingStale(true);
          }}
          leaveToday={leaveToday}
          actionsRef={actionsRef}
        />
      )}
      {checkoutOpen && clock.active && (
        <CheckoutModal
          seconds={clock.totalSeconds}
          onConfirm={confirmCheckout}
          onCancel={() => setCheckoutOpen(false)}
        />
      )}
      {/* Closing a session from an earlier day records hours against that day
          (UI-CONVENTIONS §5, rung 2): say what it records before it does. */}
      <ConfirmDialog
        open={closingStale && stale != null}
        title={`Check out of ${staleDayLabel}?`}
        consequence={[
          records == null
            ? 'Checking out of an earlier day records what it ran, up to what was left of your contracted day that day.'
            : records < ran
              ? `It ran ${fmtDuration(ran, 'hm')}, but checking out of an earlier day records at most what was left of your contracted day: ${fmtDuration(records, 'hm')} for ${staleDayLabel}.`
              : stale?.status === 'running'
                ? `It is still running, so this records ${fmtDuration(ran, 'hm')} worked for ${staleDayLabel}.`
                : `It is paused at ${fmtDuration(ran, 'hm')} worked, and that is what is recorded for ${staleDayLabel}.`,
          'If that isn’t what you worked, go to that week afterwards and ask for a time correction on the day.',
          'Today starts fresh: check in once this is closed.',
        ]}
        confirmLabel="Check out"
        onConfirm={closeStale}
        onCancel={() => setClosingStale(false)}
      />

      <Card label="My timesheet">
        {/* Under the heading rather than beside it, and wrapping: the stepper
            and Claim hours are wider than a phone's card header. */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--hf-space-3)',
            marginBottom: 'var(--hf-space-3)',
          }}
        >
          {/* Names the week by its date. The grid said "This week" over seven
              weekday initials and never which dates they were (TS-12). */}
          <PeriodStepper
            label={weekHeader(wkStart, weekStartsOn)}
            labelWidth="8.5rem"
            unit="week"
            onPrev={() => toWeek(weekOffset - 1)}
            onNext={() => toWeek(Math.min(0, weekOffset + 1))}
            nextDisabled={sameWeek}
            resetLabel="This week"
            onReset={sameWeek ? undefined : () => toWeek(0)}
          />
          {/* R5. Beside the week rather than inside a day cell: credited time is
              claimed against a date, not against an entry that exists. */}
          <Button
            variant="secondary"
            size="sm"
            icon="award"
            className="hf-hit"
            onClick={() => setClaiming(true)}
          >
            Claim hours
          </Button>
        </div>
        {/* The week's figures, under the stepper that picks the week (TS-19).
            They were a strip of four glass tiles between the clock and this
            card: a task view's KPI strip (UI-CONVENTIONS §8), apart from the
            control that changes them. Inside the card they are bare figures.
            "Hours today" went: its number is today's column below (and the
            clock above), its bars were the Daily total row, and on a past week
            it was the Total. The ring went too: it drew the % beside it.
            Capped, so three figures don't stretch across a 1440 card. */}
        <div style={{ maxWidth: '42rem', marginBottom: 'var(--hf-space-4)' }}>
          <KpiGrid>
            {/* No ring: the contract is a fixed target, not progress. The sub-figure
                is this WEEK's required hours — leave and holidays already
                deducted — not a flat ×5. */}
            {/* `org day` when HR never wrote profiles.contract_hrs_per_day: personDay
                falls back to the org's standard (lib/orgHours.ts:83), so the figure is
                correct but is NOT this person's contract. Without it, someone on a 7h
                contract that was never saved reads 8.0h here and a legitimate 0.0h
                overtime beside it, with nothing on screen explaining either. The
                monthly table has said this all along (MonthlySummary.tsx:82). */}
            <KpiTile
              label="Contracted / day"
              value={noFigures || week === undefined ? null : hrs(perDay)}
              unit="h"
              // A note, not a trend: it says what the figure is. "that week" on a
              // past week — it read "40h this week" two weeks back (TS-11).
              note={
                week
                  ? `${week.usesOrgDay ? 'org day · ' : ''}${hrs(week.requiredHours)}h ${sameWeek ? 'this' : 'that'} week`
                  : undefined
              }
            />
            <KpiTile
              label={sameWeek ? 'vs standard · today' : 'vs standard'}
              value={noFigures || week === undefined ? null : String(targetPct)}
              unit="%"
              // Both numbers the % is made of: "Hours today" went, and on a
              // past week the standard is that week's required hours.
              note={
                noFigures || week === undefined
                  ? undefined
                  : `${hrs(basisHours)}h of ${hrs(basisStd)}h`
              }
            />
            {/* No bars: the day-by-day overtime is the grid's Overtime row, under
                the day it belongs to. Seven unlabelled bars sat above the grid at
                their own spacing, so today's amber bar landed over another
                day's column. */}
            <KpiTile
              // The label carries the TENSE and the note carries the status. "so
              // far" is not decoration: it is the only thing that makes a number
              // which is still growing an honest one.
              label={
                otOn && sameWeek
                  ? useLive && otShown != null
                    ? 'Overtime · so far'
                    : 'Overtime · today'
                  : 'Overtime'
              }
              // Live while the day is open, the ledger once it has settled, and the
              // stub whenever neither can be known — never a confident zero.
              // lib/overtime mirrors accrue_overtime's four branches, two of which do
              // NOT subtract the contracted day, so this cannot contradict the
              // overtime leave credited.
              value={noFigures || !otOn || otShown == null ? null : hrs(otShown)}
              unit="h"
              note={noFigures ? undefined : otNote}
            />
          </KpiGrid>
          {targetFailed && !weekFailed && (
            <div style={{ marginTop: 'var(--hf-space-3)' }}>
              <LoadError what="your contracted hours" onRetry={retry} />
            </div>
          )}
        </div>
        {weekFailed ? (
          <LoadError what="this week’s hours" onRetry={retry} />
        ) : loading ? (
          <div role="status" style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
            Loading…
          </div>
        ) : (
          <>
            {projOrder.length === 0 && (
              <p
                style={{
                  margin: '0 0 var(--hf-space-3)',
                  fontSize: 'var(--hf-text-sm)',
                  color: 'var(--hf-ink-2)',
                }}
              >
                {/* "check in above" only where checking in is possible (TS-11). */}
                {sameWeek
                  ? 'No time logged this week yet. Check in above.'
                  : 'No time was logged in this week.'}
              </p>
            )}
            {phone ? (
              // Nine columns don't fit a phone, and scrolling them sideways hid
              // five of the seven days and their flags at 390. A phone
              // reads the week as a list instead: one day per row, its hours by
              // source, and its flag at the thumb's side as a full 44px target.
              // role="list": WebKit drops the list from VoiceOver once
              // list-style is none.
              <>
                <ol
                  role="list"
                  aria-label={`Hours by day, ${weekHeader(wkStart, weekStartsOn)}`}
                  style={{ listStyle: 'none', margin: 0, padding: 0 }}
                >
                  {dayTotals.map((h, i) => {
                    const key = dayKeyFor(i);
                    const pend = pendingByDay[key];
                    const dayName = fmtDayKey(key, { weekday: true });
                    const isToday = sameWeek && i === todayIdx;
                    const ot = otOn ? otDays[i] : null;
                    return (
                      <li
                        key={key}
                        data-correct-day={key}
                        aria-current={isToday ? 'date' : undefined}
                        style={{
                          ...dayRow,
                          ...(isToday
                            ? {
                                background: 'var(--hf-accent-wash)',
                                borderRadius: 'var(--hf-radius-md)',
                              }
                            : null),
                        }}
                      >
                        <div style={dayBody}>
                          <div style={dayLine}>
                            <span
                              style={{
                                color: 'var(--hf-ink)',
                                fontWeight: 'var(--hf-weight-medium)',
                              }}
                            >
                              {dayName}
                            </span>
                            {isToday && (
                              <span
                                style={{
                                  fontSize: 'var(--hf-text-xs)',
                                  color: 'var(--hf-accent-text)',
                                }}
                              >
                                today
                              </span>
                            )}
                            {pend && <Badge tone="warning">pending</Badge>}
                            <Mono
                              style={{
                                marginLeft: 'auto',
                                fontSize: 'inherit',
                                fontWeight: 'var(--hf-weight-medium)',
                                color: h ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
                              }}
                            >
                              {hrs(h)}
                            </Mono>
                          </div>
                          {/* Only the sources that day has hours from: seven
                              rows of zeros per project is the table's noise. */}
                          {projOrder
                            .filter((pid) => grid[pid][i] > 0)
                            .map((pid) => (
                              <div key={pid} style={{ ...dayLine, fontSize: 'var(--hf-text-xs)' }}>
                                <Source pid={pid} projects={projects} />
                                <Mono
                                  style={{
                                    marginLeft: 'auto',
                                    fontSize: 'inherit',
                                    color: 'var(--hf-ink-2)',
                                  }}
                                >
                                  {hrs(grid[pid][i])}
                                </Mono>
                              </div>
                            ))}
                          {/* The grid's Overtime row, as a line of its day: only
                              a day that banked some, like the sources above. */}
                          {!!ot && (
                            <div style={{ ...dayLine, fontSize: 'var(--hf-text-xs)' }}>
                              <span style={{ color: 'var(--hf-ink-2)' }}>Overtime</span>
                              <Mono
                                style={{
                                  marginLeft: 'auto',
                                  fontSize: 'inherit',
                                  color: 'var(--hf-ink-2)',
                                }}
                              >
                                {hrs(ot)}
                              </Mono>
                            </div>
                          )}
                        </div>
                        {key <= todayKey ? (
                          <IconButton
                            icon="flag"
                            size={44}
                            label={
                              pend
                                ? `Open your pending correction for ${dayName}`
                                : `Request a correction for ${dayName}`
                            }
                            onClick={() => setCorrecting(key)}
                          />
                        ) : (
                          // Keeps the totals in one column down the list.
                          <span aria-hidden="true" style={{ width: 44, flexShrink: 0 }} />
                        )}
                      </li>
                    );
                  })}
                </ol>
                <div style={{ ...dayRow, borderBottom: 0 }}>
                  <div style={dayBody}>
                    <div style={dayLine}>
                      <span style={{ color: 'var(--hf-ink-2)' }}>Week total</span>
                      <Mono
                        style={{
                          marginLeft: 'auto',
                          fontSize: 'inherit',
                          fontWeight: 'var(--hf-weight-semibold)',
                          color: 'var(--hf-accent-text)',
                        }}
                      >
                        {hrs(weekTotal)}
                      </Mono>
                    </div>
                    {otOn && (
                      <div style={dayLine}>
                        <span style={{ color: 'var(--hf-ink-2)' }}>Overtime</span>
                        <Mono
                          style={{
                            marginLeft: 'auto',
                            fontSize: 'inherit',
                            color: otWeek ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
                          }}
                        >
                          {otWeek == null ? '—' : hrs(otWeek)}
                        </Mono>
                      </div>
                    )}
                    <div style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                      A day wrong? Flag it to ask for a correction.
                    </div>
                  </div>
                  <span aria-hidden="true" style={{ width: 44, flexShrink: 0 }} />
                </div>
              </>
            ) : (
              /* Nine columns, so on a narrow tablet it scrolls in its own box;
               focusable, so the keyboard can scroll it too. Relative, or the
               headers' .hf-sr-only text escapes the clip and widens the page
               (UI-CONVENTIONS §8). */
              <div
                role="region"
                aria-label={`Hours by day, ${weekHeader(wkStart, weekStartsOn)}`}
                tabIndex={0}
                style={{ overflowX: 'auto', position: 'relative' }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 'var(--hf-text-sm)',
                  }}
                >
                  <thead>
                    <tr>
                      {/* "Source", not "Project": the only row most people have is
                        the day clock, and Projects is switched off (TS-10). */}
                      <th scope="col" style={{ ...th, textAlign: 'left', minWidth: 180 }}>
                        Source
                      </th>
                      {labels.map((d, i) => {
                        const key = dayKeyFor(i);
                        return (
                          <th
                            key={key}
                            scope="col"
                            aria-current={sameWeek && i === todayIdx ? 'date' : undefined}
                            style={{ ...th, ...todayCol(i) }}
                          >
                            <span aria-hidden="true">
                              {d}
                              <Mono dim style={{ display: 'block', fontSize: 'var(--hf-text-xs)' }}>
                                {Number(key.slice(8))}
                              </Mono>
                            </span>
                            <span className="hf-sr-only">
                              {fmtDayKey(key, { weekday: true })}
                              {sameWeek && i === todayIdx ? ', today' : ''}
                            </span>
                          </th>
                        );
                      })}
                      <th scope="col" style={{ ...th, color: 'var(--hf-ink)' }}>
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {projOrder.map((pid) => {
                      const row = grid[pid];
                      return (
                        <tr key={pid}>
                          <th
                            scope="row"
                            style={{ ...td, textAlign: 'left', fontWeight: 'inherit' }}
                          >
                            <Source pid={pid} projects={projects} />
                          </th>
                          {row.map((h, i) => (
                            <td key={i} style={{ ...td, ...todayCol(i) }}>
                              {/* Zero is "0", dimmed — in ink-3, which is still
                                text. It was a "·" in the non-text ink (TS-08). */}
                              <Mono
                                style={{
                                  fontSize: 'inherit',
                                  color: h ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
                                }}
                              >
                                {hrs(h)}
                              </Mono>
                            </td>
                          ))}
                          <td style={td}>
                            <Mono
                              style={{
                                fontSize: 'inherit',
                                color: 'var(--hf-ink)',
                                fontWeight: 'var(--hf-weight-medium)',
                              }}
                            >
                              {hrs(row.reduce((a, b) => a + b, 0))}
                            </Mono>
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <th scope="row" style={{ ...td, textAlign: 'left', fontWeight: 'inherit' }}>
                        <div style={{ color: 'var(--hf-ink-2)' }}>Daily total</div>
                        {/* What the flags are for, said once. It was a permanent
                          "Tip" banner under the table, about a hover state. */}
                        <div style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                          A day wrong? Flag it to ask for a correction.
                        </div>
                      </th>
                      {/* The day total is the number a person disputes, so the way
                        to dispute it sits under it — a button you can see, not a
                        total that turned into one on hover (TS-13). A day that has
                        not happened has nothing to correct. */}
                      {dayTotals.map((h, i) => {
                        const key = dayKeyFor(i);
                        const pend = pendingByDay[key];
                        const dayName = fmtDayKey(key, { weekday: true });
                        return (
                          <td
                            key={key}
                            data-correct-day={key}
                            style={{ ...td, ...todayCol(i), verticalAlign: 'top' }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 'var(--hf-space-1)',
                              }}
                            >
                              <Mono
                                style={{
                                  fontSize: 'inherit',
                                  color: h ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
                                }}
                              >
                                {hrs(h)}
                              </Mono>
                              {key <= todayKey && (
                                <IconButton
                                  icon="flag"
                                  label={
                                    pend
                                      ? `Open your pending correction for ${dayName}`
                                      : `Request a correction for ${dayName}`
                                  }
                                  onClick={() => setCorrecting(key)}
                                />
                              )}
                              {/* A word, in the waiting tone. It was a 5px amber dot. */}
                              {pend && <Badge tone="warning">pending</Badge>}
                            </div>
                          </td>
                        );
                      })}
                      <td style={{ ...td, verticalAlign: 'top' }}>
                        <Mono
                          style={{
                            fontSize: 'inherit',
                            fontWeight: 'var(--hf-weight-semibold)',
                            color: 'var(--hf-accent-text)',
                          }}
                        >
                          {hrs(weekTotal)}
                        </Mono>
                      </td>
                    </tr>
                    {/* Under the day it belongs to, by construction: overtime becomes
                        leave, so a figure over the wrong column is a wrong
                        claim (TS-19). No row when accrual is off — the figure
                        above says so. */}
                    {otOn && (
                      <tr>
                        <th
                          scope="row"
                          style={{
                            ...td,
                            textAlign: 'left',
                            fontWeight: 'inherit',
                            color: 'var(--hf-ink-2)',
                          }}
                        >
                          Overtime
                        </th>
                        {otDays.map((h, i) => (
                          <td key={i} style={{ ...td, ...todayCol(i) }}>
                            <Mono
                              style={{
                                fontSize: 'inherit',
                                color: h ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
                              }}
                            >
                              {h == null ? '—' : hrs(h)}
                            </Mono>
                          </td>
                        ))}
                        <td style={td}>
                          <Mono
                            style={{
                              fontSize: 'inherit',
                              fontWeight: 'var(--hf-weight-medium)',
                              color: otWeek ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
                            }}
                          >
                            {otWeek == null ? '—' : hrs(otWeek)}
                          </Mono>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      {uid && claiming && (
        <CompHoursModal
          uid={uid}
          categories={compCats}
          today={isoDay(orgToday(tz))}
          onClose={() => setClaiming(false)}
          onFiled={() => void refresh()}
        />
      )}
      {uid && correcting && editDayOn && (
        <EditDayDialog
          profileId={uid}
          date={correcting}
          pickDate
          onClose={() => setCorrecting(null)}
          onDone={() => void refresh()}
        />
      )}
      {uid && correcting && !editDayOn && (
        <CorrectionModal
          uid={uid}
          workDate={correcting}
          entries={correctingEntries}
          tz={tz}
          pending={pendingByDay[correcting]}
          onClose={() => setCorrecting(null)}
          onChanged={() => void refresh()}
        />
      )}

      {/* Your own month — the same row, and the same arithmetic, that HR sees in
          the org-wide table and the export.

          `onlyProfileId` is not optional here. RLS scopes these rows to the
          VIEWER, so for a member it does return exactly one row — but an
          owner/admin/HR holds people:full + timesheets:full and gets the entire
          org, which turned this personal screen into a second copy of the HR
          table for exactly the people who already have one. Scope on the screen's
          own meaning, never on what the viewer's role happens to permit. */}
      {uid && <MonthlySummary tz={tz} org={std} workdays={workdays} onlyProfileId={uid} />}

      {/* The check-in / check-out times behind those totals. Reported (timesheets,
          2026-09-01) as wanting "actual time entries rather than a vague hour
          entries" — the summary above says a day was 7.4h without ever saying
          when it started or ended, and the two are not derived from the same
          thing: attendance.hours comes from logged time_entries, the arrival
          from the day clock. Same component HR reads on a person, mounted on the
          viewer's own id; every table it reads is self-readable under RLS
          (time_entries, attendance, leave_requests, time_entry_pauses all admit
          `= auth.uid()`), so this needs no new grant.

          Your correction requests and compensated-hours claims render inside
          the day they are about (they used to be two cards of their own, where a
          corrected day read here as an 11-hour pause). Cancel lives there too. */}
      {uid && (
        <DayClockLog
          profileId={uid}
          reloadKey={loadNonce}
          onChanged={() => void refresh()}
          onEditDay={editDayOn ? (k) => setCorrecting(k ?? isoDay(orgToday(tz))) : undefined}
        />
      )}
    </div>
  );
}

const th: CSSProperties = {
  padding: 'var(--hf-space-2) var(--hf-space-3)',
  textAlign: 'center',
  fontSize: 'var(--hf-text-2xs)',
  fontWeight: 'var(--hf-weight-medium)',
  color: 'var(--hf-ink-3)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--hf-track-eyebrow)',
  borderBottom: '1px solid var(--hf-rule)',
};
const td: CSSProperties = {
  padding: 'var(--hf-space-2) var(--hf-space-3)',
  textAlign: 'center',
  borderBottom: '1px solid var(--hf-rule)',
};
// The phone's day list: a day, its sources under it, the flag to the right.
const dayRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--hf-space-2)',
  padding: 'var(--hf-space-1) 0 var(--hf-space-1) var(--hf-space-3)',
  borderBottom: '1px solid var(--hf-rule)',
};
const dayBody: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--hf-space-1)',
  padding: 'var(--hf-space-2) 0',
  fontSize: 'var(--hf-text-md)',
};
const dayLine: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: 'var(--hf-space-1) var(--hf-space-2)',
};
